import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { decodeToken } from "react-jwt";

import { useNotification } from "../../../../hooks/useNotification";
import { Eye, EyeSlash } from "../../../../assets/svg";
import { Input } from "../../../../components/Input";
import {
  Title,
  Form,
  LinkSignIn,
  Musthave,
  Rule,
  SuccessSignUp,
  ContentConfirmSignUp,
} from "./styles";
import {
  IConfirmAccount,
  IFormSignUp,
  IOptionTypeUsername,
} from "../../interfaces";
import { Button } from "../../../../components/Button";
import { LayoutAuth } from "../../../../components/LayoutAuth";
import { IRequestSignUp } from "../../../../common/interfaces";
import { validatePassword } from "../../../../utils/regex";
import { Switch } from "../../../../components/Switch";
import { Phone } from "../../../../components/PhoneInput";
import { useAuth } from "../../../../hooks/useAuth";
import { AxiosError } from "axios";
import { getToken } from "../../../../support/storage/BrowserStorage";
import { DataTokenJwt } from "../../../../hooks/interfaces";
import { TypeUsername } from "../../enum";
import { useTranslate } from "../../../../hooks/useTranslate";

const SignUp = () => {
  const {
    signUp,
    signUpCongnitoProfile,
    confirmAccount,
    signIn,
    resendCode,
    createAccountProfile,
    createUser,
    loadUser,
    signOut,
    idUserCollaborator,
    setIdUserCollaborator,
  } = useAuth();
  const navigate = useNavigate();
  const { errorNotification, successNotification } = useNotification();
  const { translate } = useTranslate();

  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [dataConfirmAccount, setDataConfirmAccount] = useState<IConfirmAccount>(
    {} as IConfirmAccount
  );
  const [isLoading, setIsLoading] = useState(false);
  const [signType, setSignType] = useState<TypeUsername>(TypeUsername.email);
  const [step, setStep] = useState(1);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [rulesPassword, setRulesPassword] = useState({
    length_char: false,
    upper_case: false,
    lower_case: false,
    number: false,
    special_char: false,
  });

  const schema = yup.object({
    [signType]:
      signType === TypeUsername.email
        ? yup
            .string()
            .email(translate("global.email_invalid"))
            .required(translate("global.email_required"))
        : yup.string().required(translate("global.phone_required")),
    password: yup.string().required(translate("global.password_required")),
    name: yup.string().required(translate("global.name_required")),
  });

  const {
    register,
    control,
    handleSubmit,
    unregister,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<IFormSignUp>({
    resolver: yupResolver(schema),
  });

  const createUserForm = async ({
    password,
    email,
    phone,
    name,
  }: IFormSignUp) => {
    setIsLoading(true);

    const body: IRequestSignUp = { password, userName: email ?? phone };

    if (email) body.email = email;
    if (phone) body.phone = phone;

    try {
      setDataConfirmAccount({
        userName: body.userName!,
        password,
        name,
      });

      if (idUserCollaborator) {
        await signUpCongnitoProfile({
          ...body,
          idUser: idUserCollaborator,
        });
        successNotification(translate("auth.success_message"));
        await confirmUser({
          name,
          password,
          userName: body.userName!,
        });
      } else {
        await signUp(body);
        successNotification(translate("global.success_send_code"));
        setStep(2);
        setIsLoading(false);
      }

      setIdUserCollaborator("");
    } catch (error) {
      const err = error as AxiosError;
      setIdUserCollaborator("");
      setIsLoading(false);
      if (err.response?.status === 409) {
        errorNotification(translate("auth.error_new_code"));

        await resendCode(body.userName!);
        setStep(2);
        return;
      }
      if (err.response?.status === 400) {
        const keyTranslate =
          signType === TypeUsername.email
            ? "auth.error_email_exists"
            : "auth.error_phone_exists";
        errorNotification(translate(keyTranslate));
        return;
      }
      errorNotification(translate("auth.error_create_account"));
    }
  };

  const confirmUser = async (data: IConfirmAccount) => {
    try {
      await signIn({
        userName: data.userName,
        password: data.password,
      });

      if (!idUserCollaborator) {
        const token = getToken();

        const { sub } = decodeToken(token!) as DataTokenJwt;

        await createAccountProfile({
          identity: sub,
          attributes: [
            { type: "name", value: data.name },
            {
              type: signType,
              value: data.userName,
            },
          ],
        });
      }

      await createUser({
        name: data.name,
        [signType]: data.userName,
      });

      await loadUser();

      successNotification(translate("auth.success_account_message"));
      setIsLoading(false);
      setIdUserCollaborator("");
      navigate("/content-center");
    } catch (error) {
      const err = error as AxiosError;
      setIsLoading(false);
      if (err.response?.status === 403) {
        errorNotification(translate("auth.error_invalid_code"));
        return;
      }
      errorNotification(translate("auth.error_confirm_account"));
      signOut();
    }
  };

  const confirmUserForm = async () => {
    setIsLoading(true);
    try {
      await confirmAccount({
        confirmationCode: code,
        userName: dataConfirmAccount.userName,
      });

      await confirmUser(dataConfirmAccount);
    } catch (error) {
      errorNotification(translate("auth.error_confirm_account"));
      signOut();
    }
  };

  const options: IOptionTypeUsername[] = [
    { value: TypeUsername.email, label: translate("global.email") },
    { value: TypeUsername.phone, label: translate("global.phone") },
  ];

  const selectSignType = (value: TypeUsername) => {
    unregister(value === TypeUsername.email ? "phone" : "email");
    setSignType(value);
  };

  const checkPassword = (value: string) => {
    if (!value) return;

    const { length_char, upper_case, lower_case, number, special_char } =
      validatePassword(value);

    if (
      [length_char, upper_case, lower_case, number, special_char].every(
        (element) => element
      )
    ) {
      clearErrors("password");
    } else {
      setError("password", { message: translate("global.password_invalid") });
    }

    setValue("password", value);
    setRulesPassword({
      length_char,
      upper_case,
      lower_case,
      number,
      special_char,
    });
  };

  return (
    <LayoutAuth>
      <Title>
        {step === 1
          ? translate("auth.title_1_sign_up")
          : translate("auth.title_2_sign_up")}
      </Title>

      {step === 1 && (
        <>
          <Switch
            currentActive={signType}
            options={options}
            onChange={(value) => {
              selectSignType(value as any);
            }}
          />
          <Form onSubmit={handleSubmit(createUserForm)}>
            {signType === TypeUsername.email ? (
              <Input
                {...register("email")}
                error={errors.email?.message}
                placeholder={translate("global.placeholder_email")}
                type="email"
              />
            ) : (
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Phone
                    value={value}
                    placeholder={translate("global.placeholder_phone")}
                    onChange={onChange}
                    defaultCountry="BR"
                    error={errors.phone?.message}
                  />
                )}
              />
            )}
            <Input
              {...register("name")}
              error={errors.name?.message}
              placeholder={translate("global.placeholder_name")}
            />
            <Input
              {...register("password")}
              onChange={(e) => {
                checkPassword(e.target.value);
              }}
              error={errors.password?.message}
              placeholder={translate("global.placeholder_password")}
              onFocus={() => {
                setShowVerifyPassword(true);
              }}
              onBlur={() => {
                setShowVerifyPassword(false);
              }}
              type={showPassword ? "text" : "password"}
              cbIcon={() => {
                setShowPassword(!showPassword);
              }}
              icon={showPassword ? <EyeSlash /> : <Eye />}
            />
            <Musthave isVisible={showVerifyPassword}>
              <Rule isCheked={rulesPassword.upper_case}>
                {translate("global.password_rule_1")}
              </Rule>
              <Rule isCheked={rulesPassword.lower_case}>
                {translate("global.password_rule_2")}
              </Rule>
              <Rule isCheked={rulesPassword.number}>
                {translate("global.password_rule_3")}
              </Rule>
              <Rule isCheked={rulesPassword.special_char}>
                {translate("global.password_rule_4")}
              </Rule>
              <Rule isCheked={rulesPassword.length_char}>
                {translate("global.password_rule_5")}
              </Rule>
            </Musthave>
            <Button
              label={translate("auth.title_1_sign_up")}
              type="submit"
              isLoading={isLoading}
            />
          </Form>
          <LinkSignIn>
            <Link to="/">{translate("auth.cta_login")}</Link>
          </LinkSignIn>
        </>
      )}

      {step === 2 && (
        <ContentConfirmSignUp>
          <SuccessSignUp>
            {translate(
              signType === TypeUsername.email
                ? "auth.message_send_code_email"
                : "auth.message_send_code_phone"
            )}
          </SuccessSignUp>

          <Input
            onChange={(event) => {
              setCode(event.target.value);
            }}
            value={code}
            placeholder={translate("global.placeholder_code")}
            type="number"
          />

          <Button
            label={translate("auth.title_2_sign_up")}
            type="button"
            disabled={code.length < 6}
            onClick={async () => {
              await confirmUserForm();
            }}
            isLoading={isLoading}
          />

          <LinkSignIn>
            <Link to="/">{translate("auth.cta_login")}</Link>
          </LinkSignIn>
        </ContentConfirmSignUp>
      )}
    </LayoutAuth>
  );
};

export { SignUp };
